/**
 * The three pieces that turn one graded team into a room being built.
 *
 * Ben: "push people to submit their entire league... let them slowly build their
 * league one team at a time." So the report is no longer the end of the road:
 * above it a switcher between the teams already entered, under that a strip
 * saying how much of the room is in, and after it a card offering the next one.
 *
 * 🔴 THE STRIP GRADES NOTHING UNTIL THE ROOM IS FULL, AND SAYS SO. Three of ten
 * teams ranked against each other is a ranking of the teams somebody happened to
 * type in: the best of three is not the best in the league, and a letter grade
 * claiming otherwise would be the page lying with a straight face. So the strip
 * shows projected points and surplus - facts about each team on its own - and
 * names the count. The room letters arrive with the last team, not before.
 *
 * 🔴 AND NOTHING HERE ASKS FOR AN EMAIL AGAIN. The unlock is once ever per
 * browser (rateTeamStore's header says why); a reader adding their sixth team
 * has answered that question five teams ago.
 */

import { useState } from "react";
import { theme } from "../../data/theme.js";
import { C, SZ, card, primaryBtn } from "../Submit/submitStyles.js";
import { roomRowOrder } from "../../utils/roomOrder.js";
import TeamListRow from "./TeamListRow.jsx";

/**
 * The seats already entered, as cards you can tap between.
 *
 * 🔴 THESE WERE NAME-ONLY PILLS AND ARE NOW THE SAME ROW THE STRIP BELOW DRAWS,
 * at the compact density. Ben, on the deployed room report: the list was
 * nondescript. It was — a switcher between ten auction teams that said nothing
 * about any of them, on a page whose whole subject is what those teams did with
 * their money. Each chip now carries its seat's letter when the room has earned
 * one, its market surplus, and the mini spend bar, so choosing a seat is a
 * comparison rather than a guess at a name.
 *
 * `rows` is OPTIONAL and it is what makes a chip rich. The roster step draws
 * this control before anything has been graded and passes none, which the row
 * renders as a name and nothing else — never as an unpriced or zeroed team.
 *
 * 🔴 RANK ORDER, NOT SEAT ORDER (Ben, Sep 3 2026). This control used to render
 * in entry order, on the argument that a seat should not move under the finger
 * reaching for it. That argument lost: with the chips carrying letters, the A+
 * sat ninth, and a switcher that shows a grade on every chip and orders them by
 * nothing is a leaderboard with its rows shuffled. It now takes the SAME order
 * the room report and the strip below take — roomOrder.js, one sort, three
 * consumers. `onPick` still receives the caller's own seat index, so what moved
 * is the reading order and not the wiring.
 */
export function TeamSwitcher({ teams, active, onPick, rows }) {
  const order = roomRowOrder(rows || teams);
  return (
    <div
      data-testid="team-switcher"
      role="tablist"
      /* 🔴 A GRID, NOT A WRAPPING FLEX ROW. Ben, on the deployed page: the strip
         wraps two cramped cards per row. Ragged wrapping was half of that — ten
         chips of ten different widths do not line up into anything a reader can
         compare down a column — and the page's 640px column was the other half
         (RatePage's RATE_REPORT_COLUMN carries that fix). auto-fill with a real
         minimum means the chip width is a floor rather than an accident: two
         columns on a phone, four in the report column, and never one squeezed
         to fit a long team name beside a short one. */
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: 8,
        marginBottom: 10,
      }}
    >
      {order.map((i, place) => {
        const t = teams[i];
        const row = rows ? rows[i] : null;
        return (
          <TeamListRow
            key={t.name}
            density="compact"
            /* The stagger counts down the list as drawn, not as entered. */
            index={place}
            role="tab"
            ariaSelected={i === active}
            testId={`team-chip-${t.name}`}
            name={t.name}
            active={i === active}
            onClick={() => onPick(i)}
            hasReport={!!row}
            badge={row ? row.grade : null}
            badgeIsGrade={!!(row && row.grade)}
            surplus={row ? row.surplus : null}
            spent={row ? row.spent : 0}
            breakdown={row ? row.breakdown : null}
            build={row ? row.build : null}
          />
        );
      })}
    </div>
  );
}

/**
 * One team's facts, in the shape both controls above read.
 *
 * Exported because TeamTab builds the same rows once for the switcher and the
 * strip — two lists of the same teams that disagreed about a surplus would be
 * the same class of bug the one-report merge was written to end.
 *
 * `grade` is NOT computed here and never can be: a letter is a claim about a
 * full, fully priced room and only the caller knows whether it has one. `build`
 * is the opposite — a fact about one roster's own money that needs nothing from
 * the rest of the room — so it is computed from what is passed in and is simply
 * null for a roster with no dollars in it.
 */
export function teamRow(name, report, breakdown, build) {
  return {
    name,
    week1: report && report.week1 != null ? report.week1 : null,
    surplus: report && report.hasPrices ? report.surplus : null,
    spent: report ? report.spent : 0,
    breakdown: breakdown || null,
    build: build || null,
    grade: null,
    // 🔴 THE RANK RIDES WITH THE LETTER, and for the same reason: it is a claim
    // about a full, fully priced room, so only the caller can set it. It is
    // what roomOrder.js sorts a graded room on — carrying the letter without
    // its rank would leave every list ordering an A+ room by projected points.
    rank: null,
  };
}

/**
 * "Your room so far": the entered teams, best first, and the count.
 *
 * Ordered by roomOrder.js — the letter's rank once the room has letters, and
 * projected week 1 points until then, which is the one number every entered team
 * has, priced or not. Surplus rides along on the teams that are priced, and is
 * simply absent on the ones that are not rather than being shown as zero.
 *
 * The sort used to live in this function. It moved out when the switcher above
 * started needing the same one — see roomOrder.js's header.
 */
export function RoomSoFar({ teams, rows: built, teamCount, onOpenRoom }) {
  const list = built || [];
  const rows = roomRowOrder(list).map((i) => list[i]);
  const full = teams.length >= teamCount && teamCount > 0;

  return (
    <div data-testid="room-so-far" style={{ ...card, marginBottom: 14 }}>
      <div style={{ fontSize: SZ.body, fontWeight: 800 }}>Your room so far</div>
      <div style={{ fontSize: SZ.small, color: C.text.secondary, marginTop: 2 }}>
        {teams.length} of {teamCount} {teamCount === 1 ? "team" : "teams"} entered
      </div>
      {/* 🔴 THE SAME ROW THE SWITCHER ABOVE DRAWS, at the full density: rank,
          badge, name, points, surplus and the mini spend bar. The badge is the
          DASH until the room is full and graded, which is this strip's whole
          rule stated in a mark rather than only in the fine print below — the
          leading number is the reading order on projected points, not a grade,
          and it must never be mistaken for one. */}
      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
        {rows.map((row, i) => (
          <TeamListRow
            key={row.name}
            density="full"
            index={i}
            testId={`room-row-${row.name}`}
            rank={i + 1}
            name={row.name}
            badge={row.grade || null}
            badgeIsGrade={!!row.grade}
            week1={row.week1}
            surplus={row.surplus}
            spent={row.spent}
            breakdown={row.breakdown}
            build={row.build}
          />
        ))}
      </div>
      {full ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={onOpenRoom} style={{ ...primaryBtn(true), width: "100%" }}>
            Open the full room report
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 10, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
          Room grades wait for a full room. Ranking {teams.length} of {teamCount} teams against each
          other would rank the teams you happened to enter, not the league, so these are each team's
          own projected points and its own market surplus until every seat is in.
        </div>
      )}
    </div>
  );
}

/**
 * "Add another team in this league": same settings, fresh ladder, a name.
 *
 * The name is editable and pre-filled, because most people will keep the
 * default and the ones who will not should not have to hunt for the field. It
 * is the ONLY thing this card asks for: the league is already described, and
 * asking anything else would be re-describing it once per seat.
 */
export function AddTeamCard({ defaultName, onAdd }) {
  const [name, setName] = useState(defaultName);
  const clean = name.trim() || defaultName;
  return (
    <div data-testid="add-team" style={{ ...card, marginTop: 14 }}>
      <div style={{ fontSize: SZ.body, fontWeight: 800 }}>Add another team in this league</div>
      <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5, margin: "4px 0 10px" }}>
        Same league, same scoring, a blank roster. Enter every seat and the whole room gets graded
        against itself.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Team name"
          maxLength={40}
          autoComplete="off"
          style={{
            flex: "1 1 160px",
            boxSizing: "border-box",
            padding: "10px 12px",
            // 16px keeps iOS Safari from zooming the viewport on focus.
            fontSize: 16,
            borderRadius: theme.radius.lg,
            background: C.bg.deep,
            border: `1px solid ${C.border.default}`,
            color: C.text.primary,
          }}
        />
        <button
          type="button"
          onClick={() => onAdd(clean)}
          style={{ ...primaryBtn(true), flex: "1 1 140px" }}
        >
          Add this team
        </button>
      </div>
    </div>
  );
}
